import { normalizeAllDayBounds } from "../../../lib/all-day";
import { intervalsOverlap } from "../../../lib/intervals";

/**
 * Pure layout helper for translucent linked-event overlays in the
 * scheduler's week + day calendar views (Chunk E4).
 *
 * Inputs:
 *  - overlays: pre-classified Overlay records (see shape below).
 *  - pollSlots: { id, start, end } for the current poll (RBC events).
 *  - viewKey: "week" | "day" — drives the right-lane width tier.
 *  - visibilityMode: "hide" | "overlapping" | "all".
 *
 * Output: the same overlays, each augmented with:
 *   - laneStyle: positioning hints for absolute placement inside the
 *       day column (right-aligned mirror gap), or `null` for all-day.
 *   - visible: whether the consumer should render this overlay given
 *       the current visibility mode.
 *
 * NOTE: `kind` here is the overlay's visual class ("conflict" |
 * "preferred-align" | "feasible-align"); this is a separate namespace
 * from `voteAutoReason.kind` even though the strings happen to match.
 *
 * Overlay record shape (input):
 * {
 *   id: string,
 *   kind: "conflict" | "preferred-align" | "feasible-align",
 *   title: string,
 *   startUtc: string (ISO),
 *   endUtc: string (ISO),
 *   allDay: boolean,
 *   tzid?: string,           // mark.tzid; null for linked-cal events
 *   deepLinkHref: string,
 *   sourceSchedulerId?: string,
 * }
 */

/**
 * Compute browser-local day-aligned start/end for an allDay overlay so
 * RBC's eventSegments (which uses local-tz `startOf(date, 'day')` and
 * `ceil(date, 'day')`) produces the correct column span regardless of the
 * mismatch between the mark's tzid and the user's browser timezone.
 *
 * Without this normalization:
 *   - A mark created in EDT (e.g. May 3 EDT, stored as
 *     start=2026-05-03T04:00Z, end=2026-05-04T04:00Z) renders correctly
 *     in EDT browsers but spans 2 columns in UTC and Europe/Asia browsers,
 *     because `startOf` sees the start at "May 3 04:00 LOCAL" which floors
 *     to "May 3 00:00 LOCAL" and `ceil` rounds the exclusive-end up to the
 *     next local-day boundary.
 *   - The H5 1ms trim only papers over the simplest case (browser tz ==
 *     mark tzid). For any other browser tz the trim either still spills
 *     into the next local day (UTC, east of mark) or leaves the start on
 *     the previous local day (west of mark).
 *
 * Strategy:
 *   1. Compute the first/last calendar-day the event covers in its source
 *      tz (`tzid` for marks, browser-local for linked-calendar events that
 *      have no tzid metadata).
 *   2. Construct browser-local Dates anchored at midnight of those days.
 *      `start = new Date(y, m-1, d)` = browser-local 00:00.
 *      `end = new Date(y, m-1, lastDay, 23, 59, 59, 999)` = browser-local
 *      end-of-last-day. The 1ms-before-next-day end keeps the day-tint
 *      walker (cursor <= last) iterating exactly the covered days.
 *
 * @param {{startUtc: string, endUtc: string, tzid?: string|null}} overlay
 * @returns {{startUtc: string, endUtc: string}|null} new ISO bounds, or
 *   null if the input is invalid (caller should skip the overlay).
 */
export function normalizeAllDayOverlayBounds(overlay) {
  if (!overlay) return null;
  // Delegates to the canonical lib/all-day.js normalizer; this wrapper keeps the
  // overlay-layout ISO-string contract (and its existing callers/tests) intact.
  const bounds = normalizeAllDayBounds({
    startUtc: overlay.startUtc,
    endUtc: overlay.endUtc,
    tzid: overlay.tzid,
  });
  if (!bounds) return null;
  return {
    startUtc: bounds.start.toISOString(),
    endUtc: bounds.end.toISOString(),
  };
}

const RIGHT_LANE_WIDTH = {
  week: "25%",
  day: "40%",
};

function overlayOverlapsAnyPollSlot(overlay, pollSlots) {
  const oStart = new Date(overlay.startUtc).getTime();
  const oEnd = new Date(overlay.endUtc).getTime();
  if (!Number.isFinite(oStart) || !Number.isFinite(oEnd) || oEnd <= oStart) {
    return false;
  }
  for (const slot of pollSlots || []) {
    const s = slot.start instanceof Date ? slot.start.getTime() : new Date(slot.start).getTime();
    const e = slot.end instanceof Date ? slot.end.getTime() : new Date(slot.end).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    if (intervalsOverlap(oStart, oEnd, s, e)) return true;
  }
  return false;
}

/**
 * Build laneStyle for a timed overlay. Returns inline-style props that
 * can be merged into RBC's eventPropGetter style result. Right-aligned
 * with a mirror gap so opaque poll slots keep the left lane.
 */
export function buildTimedOverlayLaneStyle(viewKey) {
  const width = RIGHT_LANE_WIDTH[viewKey] || RIGHT_LANE_WIDTH.week;
  return {
    right: "0",
    width,
    left: "auto",
  };
}

/**
 * Returns the layout-augmented overlays. Pure / no side effects.
 */
export function layoutCalendarOverlays({
  overlays = [],
  pollSlots = [],
  viewKey = "week",
  visibilityMode = "overlapping",
}) {
  return overlays.map((overlay) => {
    const isAllDay = !!overlay.allDay;
    let visible;
    if (visibilityMode === "hide") {
      visible = false;
    } else if (visibilityMode === "all") {
      visible = true;
    } else {
      // "overlapping" — only emit when the overlay overlaps a poll slot.
      visible = overlayOverlapsAnyPollSlot(overlay, pollSlots);
    }
    return {
      ...overlay,
      laneStyle: isAllDay ? null : buildTimedOverlayLaneStyle(viewKey),
      visible,
    };
  });
}
